import React, { Component } from 'react';
import PropTypes from 'prop-types';
import dompurify from 'dompurify';
import { Box, Flex } from '../Structure';
import Button from '../Button';
import ExternalLink from '../ExternalLink';
import Text from '../Text';
import TextEditor from '../TextEditor';
import ContentThemeStyling from './ContentThemeStyling';
import api from '../../utils/services';

const propTypes = {
  carrierId: PropTypes.number,
  edit: PropTypes.bool,
  inherit: PropTypes.bool,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  value: PropTypes.string,
  onSelectChange: PropTypes.func,
  displayTypeSelect: PropTypes.bool,
  dropDownOptions: PropTypes.arrayOf(PropTypes.object),
  indexOfContentType: PropTypes.number,
};

const defaultProps = {
  carrierId: null,
  edit: false,
  inherit: false,
  onChange: null,
  onSave: null,
  onCancel: null,
  value: null,
  onSelectChange: null,
  displayTypeSelect: false,
  dropDownOptions: null,
  indexOfContentType: null,
};

dompurify.addHook('afterSanitizeAttributes', (node) => {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

/* WARNING: Do not frequently trigger a value change while not in edit
 * mode, as the component needs to make api request to render the content
 */
export default class LiquidContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showHelp: false,
      valueRendered: null,
    };
  }

  componentDidMount() {
    const { edit } = this.props;
    if (!edit) {
      this.renderValue();
    }
  }

  componentDidUpdate(previousProps) {
    const { edit, value } = this.props;
    // TODO on exit of edit mode display a spinner or something so we don't show the
    // old value while the api is being called
    if (!edit && (previousProps.value !== value || previousProps.edit !== edit)) {
      this.renderValue();
    }
  }

  renderValue = () => {
    const { carrierId, value } = this.props;
    if (value) {
      api.renderLiquidContents(value, carrierId)
        .then(valueRendered => this.setState({ valueRendered }))
        .catch(() => this.setState({
          // TODO a more informative error message when editing
          valueRendered: 'Error rendering content',
        }));
    }
  }

  toggleHelp = () => {
    this.setState(state => ({
      showHelp: !state.showHelp,
    }));
  }

  render() {
    const {
      edit, inherit, onCancel, onChange, onSave, onSelectChange, value, displayTypeSelect,
      dropDownOptions, indexOfContentType,
    } = this.props;
    const { showHelp, valueRendered } = this.state;

    const liquidRendered = (
      // eslint-disable-next-line react/no-danger
      <div dangerouslySetInnerHTML={{ __html: dompurify.sanitize(valueRendered) }} />
    );
    let liquidThemed = liquidRendered;
    if (!inherit) {
      liquidThemed = <ContentThemeStyling>{liquidRendered}</ContentThemeStyling>;
    }

    if (!edit) {
      return liquidThemed;
    }

    return (
      <Box>
        <Text as="h3">Content Source</Text>
        <TextEditor
          onSelectChange={onSelectChange}
          displayTypeSelect={displayTypeSelect}
          dropDownOptions={dropDownOptions}
          indexOfContentType={indexOfContentType}
          value={value}
          onCancel={onCancel}
          onChange={onChange}
          onSave={onSave}
          onPreview={this.renderValue}
          placeholder="Compose custom content..."
          rows={16}
        />
        <Text as="h3">Content Preview</Text>
        {liquidThemed}
        <Flex marginTop="1em">
          <Flex.Item marginStart="auto">
            <Button minimal onClick={this.toggleHelp}>{`${showHelp ? 'Hide' : 'Show'} Help`}</Button>
          </Flex.Item>
        </Flex>
        {showHelp && (
          <React.Fragment>
            <Text as="h3">Help</Text>
            <Text>
              Liquid is a templating language that allows you to dynamically create HTML content.
              See additional details on the official
              <ExternalLink href="https://shopify.github.io/liquid/" target="_blank"> Liquid Documentation</ExternalLink>
              .
            </Text>
            {/* Warning: When updating there should be a corresponding update in the Contents
              * controller template renderer
              */}
            <Text as="h4">Available Variables</Text>
            <Text as="h5">Carrier Scope</Text>
            <ul>
              <li>carrier: Object for the current carrier</li>
              <ul>
                <li>id</li>
                <li>name</li>
              </ul>
              <li>{'plans: List<plan>'}</li>
              <ul>
                <li>cms_contract_id</li>
                <li>cms_pbp_id</li>
                <li>cms_segment_id</li>
                <li>cms_year</li>
                <li>id</li>
                <li>name</li>
                <li>region_id</li>
              </ul>
              <li>{'regions: List<region>'}</li>
              <ul>
                <li>id</li>
                <li>name</li>
              </ul>
            </ul>
            <Text as="h5">Client Scope</Text>
            <ul>
              <li>{'carriers: List<carrier>'}</li>
              <ul>
                <li>id</li>
                <li>name</li>
              </ul>
            </ul>
            <Text>Note: For all collections only the first 1000 items will be returned.</Text>
            <Text as="h4">Sample Content Source</Text>
            <Text as="h5">Carrier Scope</Text>
            <Text>{'<span>The current carrier is: <strong>{{ carrier.name }}</strong></span>'}</Text>
            <Text as="h5">Client Scope</Text>
            <Text noMargins>{'<div>A list of carriers will appear in a box.</div>'}</Text>
            <Text noMargins>{'<div style="padding: 10px; border: 3px solid #ff0000;">'}</Text>
            <Text noMargins padding="33px">{'<ul>'}</Text>
            <Text noMargins>{'{% for carrier in carriers %}'}</Text>
            <Text noMargins>{'<li>{{ carrier.name }}</li>'}</Text>
            <Text noMargins>{'{% endfor %}'}</Text>
            <Text noMargins>{'</ul>'}</Text>
            <Text noMargins>{'</div>'}</Text>
          </React.Fragment>
        )}
      </Box>
    );
  }
}

LiquidContent.propTypes = propTypes;
LiquidContent.defaultProps = defaultProps;
